import { FALLBACK, normalizeLang, SupportedLang } from "./languages";

export const STORAGE_KEY = "uysot_lang";

/**
 * `lang` is the documented parameter. The rest are silent fallbacks so a host
 * that guesses `?locale=ru` works instead of quietly rendering Uzbek — the
 * naming is inconsistent across embeddable widgets in the wild.
 */
const LANG_PARAMS = ["lang", "locale", "lng", "hl"];

/** Only the two methods we use, so tests can pass a plain object. */
type StorageLike = Pick<Storage, "getItem" | "setItem">;

export interface LanguageSources {
  search?: string;
  storage?: StorageLike | null;
  navigatorLangs?: readonly string[];
}

/**
 * Storage is frequently partitioned or blocked inside a third-party iframe,
 * so every access is best-effort.
 */
const safeGet = (storage: StorageLike | null | undefined, key: string) => {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

export function languageFromSearch(search: string): SupportedLang | null {
  const params = new URLSearchParams(search);

  for (const name of LANG_PARAMS) {
    const lang = normalizeLang(params.get(name));
    if (lang) return lang;
  }

  return null;
}

function languageFromNavigator(
  langs: readonly string[]
): SupportedLang | null {
  for (const candidate of langs) {
    const lang = normalizeLang(candidate);
    if (lang) return lang;
  }

  return null;
}

const defaultStorage = (): StorageLike | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const defaultNavigatorLangs = (): readonly string[] =>
  typeof navigator === "undefined"
    ? []
    : navigator.languages ?? [navigator.language];

/**
 * Resolves the language to boot with, in precedence order:
 * URL > stored > browser > uz. Runs synchronously before i18n.init so the
 * first paint is already in the right language.
 *
 * A later `uysot:set-language` message from the host overrides this.
 */
export function resolveInitialLanguage(
  sources: LanguageSources = {}
): SupportedLang {
  const {
    search = window.location.search,
    storage = defaultStorage(),
    navigatorLangs = defaultNavigatorLangs(),
  } = sources;

  return (
    languageFromSearch(search) ??
    normalizeLang(safeGet(storage, STORAGE_KEY)) ??
    languageFromNavigator(navigatorLangs) ??
    FALLBACK
  );
}

export function persistLanguage(
  lang: SupportedLang,
  storage: StorageLike | null | undefined = defaultStorage()
): void {
  try {
    storage?.setItem(STORAGE_KEY, lang);
  } catch {
    // Blocked storage is expected in a partitioned iframe; the host's URL
    // parameter and postMessage remain the load-bearing paths.
  }
}
